import { useParams } from '@reach/router';
import { RowNode } from 'ag-grid-community';
import React, { Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '../../../../../../components/Layout/ContentBlock';
import NoDataContainer from '../../../../../../components/NoDataContainer';
import TableGrid, { CellRenderer, MainColumns } from '../../../../../../components/TableGrid';
import { ActionLink } from '../../../../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox.type';
import { api } from '../../../../../../core/api';
import { getActiveDataset, getActiveDbVersion } from '../../../../../../core/utils/utils';
import { FileInfo } from '../../../../../../generated/model/models';
import { useAppGoogleReCaptcha } from '../../../../../../hooks/useAppGoogleReCaptcha';
import { useBenchmarkState } from '../../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../shared/CompanyContext/CompanyContext';
import {
  useCompanyDocumentsDispatch,
  useCompanyDocumentsState,
} from '../CompanyDocumentsContext/CompanyDocumentsContext';
import {
  CompanyDocumentsContextActions,
  CompanyDocumentsContextActionTypes,
} from '../CompanyDocumentsContext/CompanyDocumentsContext.types';
import FileDownload from './FIleDownload/FileDownload';

export interface Props {
  data?: FileInfo[];
}

export interface FileRequest {
  id: string;
  fileIds: string[];
}

const translationsScope = 'companies:companyDocuments';
const columns: MainColumns[] = [
  {
    field: 'description',
    minWidth: 420,
    flex: 3,
    unSortIcon: true,
    cellRenderer: CellRenderer.FileDownload,
  },
  { field: 'date', minWidth: 150, flex: 1, unSortIcon: true, sort: 'desc' },
  { field: 'size', minWidth: 150, flex: 1, unSortIcon: true, cellRenderer: CellRenderer.Number },
  { field: 'type', minWidth: 150, flex: 1, unSortIcon: true },
];

const getActionItems = (
  queryRequest: FileRequest,
  executeRecaptcha: any,
  selectedFiles: number,
  dataset?: string,
  dbVersion?: string,
  totalElements?: number,
): ActionLink => {
  return {
    title: 'companies:companyDocuments:downloadFiles',
    snackbarReportText: 'companies:snackbarDownloadFilesText',
    onClick: async () => {
      const recaptchaToken = await executeRecaptcha('search');
      await api.company.getDocument(
        queryRequest.id,
        recaptchaToken,
        dataset,
        dbVersion,
        undefined,
        queryRequest.fileIds,
      );
    },
    count: selectedFiles > 0 ? selectedFiles : totalElements ?? 0,
    isNotActive: false,
    isHidden: false,
  };
};

export const onSelectionChanged = (
  dispatchCompanyDocuments: Dispatch<CompanyDocumentsContextActions>,
) => (ids: string[]) => {
  dispatchCompanyDocuments({
    type: CompanyDocumentsContextActionTypes.SelectFiles,
    payload: ids,
  });
};

const CompanyDocumentsList: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { selectedFiles } = useCompanyDocumentsState();
  const dispatchCompanyDocuments = useCompanyDocumentsDispatch();
  const { company, dataset, dbVersion } = useCompaniesState();
  const companyUid = company?.companyInfo?.id;
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const { dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    dataset?: string;
    dbVersion?: string;
  }>();

  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  const queryRequestData: FileRequest = {
    id: companyUid!,
    fileIds: selectedFiles,
  };

  const actionItems = useMemo(
    () =>
      getActionItems(
        queryRequestData,
        executeRecaptcha,
        selectedFiles.length,
        activeDataset,
        activeDbVersion,
        data?.length,
      ),
    [queryRequestData, selectedFiles, data, executeRecaptcha, activeDataset, activeDbVersion],
  );

  return (
    <ContentBlock
      title={t(`${translationsScope}:documents`)}
      actions={
        <FileDownload
          buttonTitle={t(`${translationsScope}:downloadFiles`)}
          actionItem={actionItems}
          totalElements={data?.length}
          selectedRows={selectedFiles.length}
        />
      }
    >
      {data && data.length ? (
        <TableGrid
          columns={columns}
          translationKey={translationsScope}
          rowSelection={true}
          rowSelectionIdGetter={rowSelectionIdGetter}
          initialSelectedRows={selectedFiles}
          onSelectionChanged={onSelectionChanged(dispatchCompanyDocuments)}
          suppressTableContextProvider={true}
          originalProps={{
            rowData: data,
            rowModelType: 'clientSide',
            domLayout: 'autoHeight',
            suppressRowClickSelection: true,
            rowSelection: 'multiple',
            defaultColDef: {
              menuTabs: [],
              sortable: true,
            },
          }}
        />
      ) : (
        <NoDataContainer />
      )}
    </ContentBlock>
  );
};

export const rowSelectionIdGetter = (node: RowNode) => {
  return node.data?.internalId ?? '';
};

export default CompanyDocumentsList;
